<template>
  <b-card class="card white-container">
    <div class="title-row">
      <span class="title-row__title">
        {{ $t('dashboard.your_overview') }}
      </span>
    </div>
    <div class="selector-row">
      <multiselect
        :value="selectedPreselectPeriod"
        @input="preselectPeriodChange"
        :options="preselectPeriodsArray"
        :preselect-first="true"
        :allow-empty="false"
        :multiple="false"
        class="selector-row__preselect"
        deselect-label=""
        select-label=""
        selected-label=""
        label="label"
        track-by="id"
      />
      <DatePickerInput
        v-model="range"
        @input="rangeChange"
        :max-date="yesterday"
        class="selector-row__datepicker"
        timezone="utc"
        is-range
      />
      <div class="selector-row__compared">
        {{ $t('dashboard.compared_to') }}
      </div>
      <multiselect
        :value="selectedComparePreselectPeriod"
        @input="comparePreselectPeriodChange"
        :options="comparePreselectPeriodsArray"
        class="selector-row__compare-period"
        :preselect-first="true"
        :allow-empty="false"
        :multiple="false"
        deselect-label=""
        select-label=""
        selected-label=""
        label="label"
        track-by="id"
      />
      <DatePickerInput
        v-if="comparePreselectPeriod !== 'No comparison'"
        v-model="compareRange"
        @input="compareRangeChange"
        :max-date="yesterday"
        class="selector-row__datepicker"
        timezone="utc"
        is-range
      />
      <multiselect
        v-model="groupBy"
        :options="groupTypes"
        class="selector-row__fragment"
        :preselect-first="true"
        :allow-empty="false"
        :multiple="false"
        deselect-label=""
        select-label=""
        selected-label=""
        label="label"
        track-by="value"
      />
    </div>
  </b-card>
</template>

<script>
import DatePickerInput from '../../components/forms/fields/datePickerInput';
import { debounce } from 'lodash';
import i18n from '@/utils/i18n';

export default {
  name: 'PeriodSelector',
  components: { DatePickerInput },
  data() {
    return {
      preselectPeriods: {
        last_7_days: i18n.t('dashboard.selector_titles.last_7_days'),
        last_4_weeks: i18n.t('dashboard.selector_titles.last_4_weeks'),
        last_3_months: i18n.t('dashboard.selector_titles.last_3_months'),
        last_12_months: i18n.t('dashboard.selector_titles.last_12_months'),
        month_to_date: i18n.t('dashboard.selector_titles.month_to_date'),
        quarter_to_date: i18n.t('dashboard.selector_titles.quarter_to_date'),
        year_to_date: i18n.t('dashboard.selector_titles.year_to_date'),
        custom: i18n.t('dashboard.selector_titles.custom'),
      },
      comparePreselectPeriods: {
        previous_period: i18n.t('dashboard.selector_titles.previous_period'),
        previous_month: i18n.t('dashboard.selector_titles.previous_month'),
        previous_quarter: i18n.t('dashboard.selector_titles.previous_quarter'),
        previous_year: i18n.t('dashboard.selector_titles.previous_year'),
        custom: i18n.t('dashboard.selector_titles.custom'),
        no_comparison: i18n.t('dashboard.selector_titles.no_comparison'),
      },
      groupTypes: [
        { label: 'Daily', value: 'day' },
        { label: 'Weekly', value: 'week' },
        { label: 'Monthly', value: 'month' },
        { label: 'Yearly', value: 'year' },
      ],
      groupBy: null,
      preselectPeriod: 'last_4_weeks',
      comparePreselectPeriod: 'previous_period',
      range: { start: null, end: null },
      compareRange: { start: null, end: null },
      changedByPreselect: false,
      changedByPreselectCompare: false,
      debounceEmit: debounce(this.emitMethod, 500),
    };
  },
  mounted() {
    this.preselectPeriodChange();
  },
  computed: {
    yesterday() {
      const today = new Date();
      today.setDate(today.getDate() - 1);
      const isoDate = today.toISOString().split('T')[0];
      return isoDate;
    },
    requestObject() {
      const formatDate = (date) =>
        (date instanceof Date ? date : new Date(date)).toISOString().split('T')[0];
      let params = { group_by: this.groupBy.value };

      const setParams = (range, prefix) => {
        if (range && range.start && range.end) {
          params[`${prefix}_start`] = formatDate(range.start);
          params[`${prefix}_end`] = formatDate(range.end);
        }
      };

      setParams(this.range, 'date');
      if (
        this.comparePreselectPeriod == 'no_comparison' ||
        (this.comparePreselectPeriod == 'custom' &&
          (!this.compareRange.start || !this.compareRange.end))
      ) {
        params.do_not_compare = true;
      } else {
        setParams(this.compareRange, 'compare');
      }

      return params;
    },
    preselectPeriodsArray() {
      return Object.entries(this.preselectPeriods).map(([id, label]) => ({ id, label }));
    },
    selectedPreselectPeriod() {
      return this.preselectPeriodsArray.find((period) => period.id === this.preselectPeriod);
    },
    comparePreselectPeriodsArray() {
      return Object.entries(this.comparePreselectPeriods).map(([id, label]) => ({ id, label }));
    },
    selectedComparePreselectPeriod() {
      return this.comparePreselectPeriodsArray.find(
        (period) => period.id === this.comparePreselectPeriod,
      );
    },
  },
  methods: {
    getDateRange(input) {
      const now = new Date();
      now.setDate(now.getDate() - 1);

      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      const end = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999),
      );

      switch (input) {
        case 'last_7_days':
          start.setUTCDate(now.getUTCDate() - 6);
          break;
        case 'last_4_weeks':
          start.setUTCDate(now.getUTCDate() - 27);
          break;
        case 'last_3_months':
          start.setUTCMonth(now.getUTCMonth() - 2);
          start.setUTCDate(1);
          break;
        case 'last_12_months':
          start.setUTCFullYear(now.getUTCFullYear() - 1);
          start.setUTCMonth(now.getUTCMonth());
          start.setUTCDate(1);
          break;
        case 'month_to_date':
          start.setUTCDate(1);
          break;
        case 'quarter_to_date':
          start.setUTCMonth(Math.floor(now.getUTCMonth() / 3) * 3);
          start.setUTCDate(1);
          break;
        case 'year_to_date':
          start.setUTCMonth(0, 1);
          break;
        default:
          return null;
      }

      return {
        start: start.toISOString(),
        end: end.toISOString(),
      };
    },
    getComparisonDateRange(input, start, end) {
      if (input === 'no_comparison') {
        return { start: null, end: null };
      }

      let startDate = new Date(start);
      let endDate = new Date(end);
      let diffDays = (endDate - startDate) / (1000 * 60 * 60 * 24) + 1;

      switch (input) {
        case 'previous_period':
          startDate.setUTCDate(startDate.getUTCDate() - diffDays);
          endDate.setUTCDate(endDate.getUTCDate() - diffDays);
          break;
        case 'previous_month':
          startDate.setUTCMonth(startDate.getUTCMonth() - 1);
          endDate.setUTCMonth(endDate.getUTCMonth() - 1);
          break;
        case 'previous_quarter':
          startDate.setUTCMonth(startDate.getUTCMonth() - 3);
          endDate.setUTCMonth(endDate.getUTCMonth() - 3);
          break;
        case 'previous_year':
          startDate.setUTCFullYear(startDate.getUTCFullYear() - 1);
          endDate.setUTCFullYear(endDate.getUTCFullYear() - 1);
          break;
        default:
          return { start: null, end: null };
      }

      return {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      };
    },
    rangeChange() {
      if (this.changedByPreselect) {
        this.changedByPreselect = false;
      } else {
        this.preselectPeriod = 'custom';
      }

      if (
        this.comparePreselectPeriod !== 'custom' &&
        this.comparePreselectPeriod !== 'no_comparison'
      ) {
        this.compareRange = this.getComparisonDateRange(
          this.comparePreselectPeriod,
          this.range.start,
          this.range.end,
        );
      }
      this.debounceEmit();
    },
    compareRangeChange() {
      if (this.changedByPreselectCompare) {
        this.changedByPreselectCompare = false;
      } else {
        this.comparePreselectPeriod = 'custom';
      }

      this.debounceEmit();
    },
    preselectPeriodChange(selected) {
      this.preselectPeriod = selected ? selected.id : 'last_4_weeks';
      this.changedByPreselect = true;
      this.range = this.getDateRange(this.preselectPeriod);
    },
    comparePreselectPeriodChange(selected) {
      this.comparePreselectPeriod = selected ? selected.id : 'previous_period';
      this.changedByPreselectCompare = true;
      if (this.range) {
        this.compareRange = this.getComparisonDateRange(
          this.comparePreselectPeriod,
          this.range.start,
          this.range.end,
        );
      }
      this.compareRangeChange();
    },
    emitMethod() {
      this.$emit('input', this.requestObject);
    },
  },
  watch: {
    groupBy() {
      this.debounceEmit();
    },
  },
};
</script>
<style lang="scss" scoped>
.card {
  padding: 0;
}
.title-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  &__title {
    font-size: 22px;
    font-weight: 500;
  }
}

.selector-row {
  display: flex;
  align-items: center;
  & > * {
    margin: 0 8px;
  }

  & > *:first-child {
    margin-left: 0;
  }
  & > *:last-child {
    margin-right: 0;
  }

  &__compared {
    white-space: nowrap;
  }

  &__preselect {
    white-space: nowrap;
    flex: 1;
  }

  &__datepicker {
    flex: 2;
    white-space: nowrap;
  }

  &__compare-period {
    flex: 1;
    white-space: nowrap;
  }

  &__fragment {
    white-space: nowrap;
    flex: 1;
  }
  .multiselect {
    div {
      &:before {
        color: #000000;
        border: none;
        border-right: 1px solid #000000;
        border-top: 1px solid #000000;
      }
    }
  }
}
</style>
